// src/pages/StaffLeavePortal.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, getDocs, addDoc, query, where,
  Timestamp, serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useSalonAuth } from '../contexts/SalonAuthContext';
import '../styles/staff-portal.css';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function StaffLeavePortal() {
  const { salonId } = useParams();
  const { currentUser, logout } = useSalonAuth();
  const navigate = useNavigate();

  const [leaveRecords, setLeaveRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const [formData, setFormData] = useState({
    type: 'annual',
    startDate: '',
    endDate: '',
    timeType: 'full',
    startTime: '09:00',
    endTime: '10:00',
    reason: '',
    notes: ''
  });

  const effectiveStaffId = currentUser?.staffId || currentUser?.uid;

  useEffect(() => {
    if (currentUser && currentUser.role !== 'staff') {
      navigate(`/BespokeBeauty/${salonId}/admin/home`, { replace: true });
      return;
    }
    if (currentUser && effectiveStaffId && salonId) {
      loadMyLeave();
    }
  }, [currentUser, salonId, effectiveStaffId]);

  const loadMyLeave = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'leave'),
        where('staffId', '==', effectiveStaffId),
        where('salonId', '==', salonId)
      );
      const snapshot = await getDocs(q);
      const records = [];
      snapshot.forEach(d => records.push({ id: d.id, ...d.data() }));
      records.sort((a, b) => (b.startDate?.seconds || 0) - (a.startDate?.seconds || 0));
      setLeaveRecords(records);
    } catch (err) {
      setError('Failed to load leave records.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    if (name === 'type') updated.timeType = value === 'timeoff' ? 'custom' : 'full';
    setFormData(updated);
  };

  const checkAppointmentConflicts = async (startDateStr, endDateStr) => {
    const leaveStart = new Date(startDateStr); leaveStart.setHours(0, 0, 0, 0);
    const leaveEnd   = new Date(endDateStr);   leaveEnd.setHours(23, 59, 59, 999);
    try {
      const q = query(
        collection(db, 'appointments'),
        where('staffId', '==', effectiveStaffId),
        where('salonId', '==', salonId)
      );
      const snap = await getDocs(q);
      const conflictDates = new Set();
      snap.forEach(d => {
        const data = d.data();
        if (data.status !== 'approved' && data.status !== 'pending') return;
        const apptStart = data.dateTimeRequested?.toDate?.()
          ?? data.startDateTime?.toDate?.()
          ?? new Date(data.dateTimeRequested || data.startDateTime);
        if (!apptStart || isNaN(apptStart.getTime())) return;
        const apptDay = new Date(apptStart); apptDay.setHours(0, 0, 0, 0);
        if (apptDay >= leaveStart && apptDay <= leaveEnd) {
          conflictDates.add(apptDay.toLocaleDateString('en-US', {
            weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
          }));
        }
      });
      return Array.from(conflictDates).sort();
    } catch (err) {
      console.error('Error checking appointment conflicts:', err);
      return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.startDate || !formData.endDate) { setError('Start and end dates are required.'); return; }
    const startDate = new Date(formData.startDate);
    const endDate = new Date(formData.endDate);
    if (startDate > endDate) { setError('End date must be on or after start date.'); return; }
    if (!formData.reason.trim()) { setError('Reason is required.'); return; }
    if (formData.type === 'timeoff' && formData.timeType === 'custom') {
      const [sh, sm] = formData.startTime.split(':').map(Number);
      const [eh, em] = formData.endTime.split(':').map(Number);
      if (eh * 60 + em <= sh * 60 + sm) { setError('End time must be after start time.'); return; }
    }

    // Check appointment conflicts
    setSaving(true);
    const apptConflicts = await checkAppointmentConflicts(formData.startDate, formData.endDate);
    if (apptConflicts.length > 0) {
      setError(
        `You have appointment(s) booked on the following date(s) within your requested leave period:\n\n${apptConflicts.join('\n')}\n\nPlease check with the admin or manager to change these appointment dates and times before the leave can be submitted.`
      );
      setSaving(false);
      return;
    }

    try {
      const leaveData = {
        leaveId: generateUUID(),
        type: formData.type,
        startDate: Timestamp.fromDate(startDate),
        endDate: Timestamp.fromDate(endDate),
        timeType: formData.timeType,
        reason: formData.reason.trim(),
        notes: formData.notes.trim(),
        staffId: effectiveStaffId,
        staffName: currentUser?.name || '',
        salonId,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: 'staff',
        submittedByUid: currentUser?.uid
      };
      if (formData.type === 'timeoff' && formData.timeType === 'custom') {
        leaveData.startTime = formData.startTime;
        leaveData.endTime = formData.endTime;
      }

      await addDoc(collection(db, 'leave'), leaveData);
      await notifyManagers(leaveData);

      setSuccess('Leave request submitted. Your manager will review it shortly.');
      setShowForm(false);
      setFormData({ type: 'annual', startDate: '', endDate: '', timeType: 'full', startTime: '09:00', endTime: '10:00', reason: '', notes: '' });
      await loadMyLeave();
    } catch (err) {
      setError('Failed to submit leave request: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const notifyManagers = async (leaveData) => {
    try {
      const snapshot = await getDocs(collection(db, 'salonUsers'));
      const managers = [];
      snapshot.forEach(d => {
        const data = d.data();
        if (data.salonId === salonId && ['owner', 'admin', 'manager'].includes(data.role)) {
          managers.push({ uid: d.id, ...data });
        }
      });
      const startStr = new Date(leaveData.startDate.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const endStr = new Date(leaveData.endDate.seconds * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const typeLabel = leaveData.type === 'annual' ? 'Annual Leave' : leaveData.type === 'medical' ? 'Medical Leave' : 'Time Off';
      for (const manager of managers) {
        await addDoc(collection(db, 'salonNotifications'), {
          type: 'leave_approval_pending',
          salonId,
          recipientUid: manager.uid,
          staffName: currentUser?.name || 'Staff',
          staffId: effectiveStaffId,
          leaveType: typeLabel,
          startDate: leaveData.startDate,
          endDate: leaveData.endDate,
          reason: leaveData.reason,
          message: `${currentUser?.name || 'A staff member'} has requested ${typeLabel} from ${startStr} to ${endStr}.`,
          isRead: false,
          createdAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.error('Error notifying managers:', err);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const statusBadge = (status) => {
    const styles = {
      pending: { background: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' },
      approved: { background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' },
      rejected: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }
    };
    const s = styles[status] || styles.pending;
    return (
      <span style={{
        ...s,
        padding: '3px 12px', borderRadius: '12px',
        fontSize: '13px', fontWeight: '600',
        fontFamily: 'Familjen Grotesk, sans-serif',
        textTransform: 'capitalize'
      }}>
        {status || 'pending'}
      </span>
    );
  };

  const handleSignOut = async () => {
    try {
      setSigningOut(true);
      await logout();
      navigate(`/BespokeBeauty/${salonId}/admin/login`, { replace: true });
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <div className="staff-portal-container">
      <div className="staff-portal-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="staff-portal-back" onClick={() => navigate(`/BespokeBeauty/${salonId}/staff/portal`)}>
            ← Back
          </button>
          <div className="staff-portal-brand">
            <span className="staff-portal-logo">BespokeBeauty</span>
            {salonId && <span className="staff-portal-salon">{salonId.replace(/-/g, ' ')}</span>}
          </div>
        </div>
        <button className="staff-portal-signout" onClick={handleSignOut} disabled={signingOut}>
          {signingOut ? 'Signing out...' : 'Sign Out'}
        </button>
      </div>

      <div className="staff-portal-body">
        {/* Page heading */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1>My Leave Records</h1>
            {currentUser?.name && (
              <p style={{ margin: '4px 0 0', fontFamily: 'Wix Madefor Text, sans-serif', fontSize: '15px', color: '#666666' }}>
                {currentUser.name}
              </p>
            )}
          </div>
          {!showForm && (
            <button className="staff-portal-action-btn" onClick={() => { setShowForm(true); setError(''); setSuccess(''); }}>
              + Add Leave Record
            </button>
          )}
        </div>

        {/* Alerts */}
        {error && <div className="staff-portal-error" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
        {success && <div className="staff-portal-success">{success}</div>}

        {/* Leave request form */}
        {showForm && (
          <div className="staff-leave-form-card">
            <h3>New Leave Request</h3>
            <form onSubmit={handleSubmit}>
              <div className="staff-form-row">
                <div className="staff-form-group">
                  <label>Leave Type <span style={{ color: '#E64C31' }}>*</span></label>
                  <select name="type" value={formData.type} onChange={handleInputChange}>
                    <option value="annual">Annual Leave</option>
                    <option value="medical">Medical Leave</option>
                    <option value="timeoff">Time Off</option>
                  </select>
                </div>
                <div className="staff-form-group">
                  <label>Time Type <span style={{ color: '#E64C31' }}>*</span></label>
                  <select name="timeType" value={formData.timeType} onChange={handleInputChange}>
                    {formData.type === 'timeoff' ? (
                      <>
                        <option value="custom">Custom Time (1+ hours)</option>
                        <option value="am">Morning (9AM–1PM)</option>
                        <option value="pm">Afternoon (1PM–End)</option>
                        <option value="full">Full Day</option>
                      </>
                    ) : (
                      <>
                        <option value="full">Full Day</option>
                        <option value="am">Morning (9AM–1PM)</option>
                        <option value="pm">Afternoon (1PM–End)</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {formData.type === 'timeoff' && formData.timeType === 'custom' && (
                <div className="staff-form-row">
                  <div className="staff-form-group">
                    <label>Start Time <span style={{ color: '#E64C31' }}>*</span></label>
                    <input type="time" name="startTime" value={formData.startTime} onChange={handleInputChange} />
                  </div>
                  <div className="staff-form-group">
                    <label>End Time <span style={{ color: '#E64C31' }}>*</span></label>
                    <input type="time" name="endTime" value={formData.endTime} onChange={handleInputChange} />
                  </div>
                </div>
              )}

              <div className="staff-form-row">
                <div className="staff-form-group">
                  <label>Start Date <span style={{ color: '#E64C31' }}>*</span></label>
                  <input type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} required />
                </div>
                <div className="staff-form-group">
                  <label>End Date <span style={{ color: '#E64C31' }}>*</span></label>
                  <input type="date" name="endDate" value={formData.endDate} min={formData.startDate} onChange={handleInputChange} required />
                </div>
              </div>

              <div className="staff-form-group">
                <label>Reason <span style={{ color: '#E64C31' }}>*</span></label>
                <input
                  type="text"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Brief reason for leave"
                  required
                />
              </div>

              <div className="staff-form-group">
                <label>Additional Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Any additional information (optional)"
                />
              </div>

              <p style={{ fontFamily: 'Wix Madefor Text, sans-serif', fontSize: '13px', color: '#888', margin: '0 0 12px' }}>
                Fields marked with <span style={{ color: '#E64C31' }}>*</span> are mandatory.
              </p>

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button type="submit" className="staff-portal-action-btn" disabled={saving}>
                  {saving ? 'Checking & Submitting…' : 'Submit Request'}
                </button>
                <button
                  type="button"
                  className="staff-portal-cancel-btn"
                  onClick={() => { setShowForm(false); setError(''); }}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Leave records list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', fontFamily: 'Wix Madefor Text, sans-serif', fontSize: '15px', color: '#666666' }}>
            Loading leave records...
          </div>
        ) : leaveRecords.length === 0 ? (
          <div className="staff-empty-state">
            <p>No leave records found.</p>
            <p>Click "Add Leave Record" to submit your first leave request.</p>
          </div>
        ) : (
          <div className="staff-leave-list">
            {leaveRecords.map(record => {
              const typeLabel = record.type === 'annual' ? 'Annual Leave' : record.type === 'medical' ? 'Medical Leave' : 'Time Off';
              const timeLabel = record.timeType === 'full' ? 'Full Day'
                : record.timeType === 'am' ? 'Morning (AM)'
                : record.timeType === 'pm' ? 'Afternoon (PM)'
                : `${record.startTime} – ${record.endTime}`;
              return (
                <div key={record.id} className="staff-leave-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div className="leave-type-label">{typeLabel}</div>
                      <div className="leave-dates">{formatDate(record.startDate)} – {formatDate(record.endDate)} · {timeLabel}</div>
                      {record.reason && <div className="leave-reason-text">{record.reason}</div>}
                    </div>
                    <div>{statusBadge(record.status)}</div>
                  </div>
                  {record.status === 'pending' && (
                    <div className="pending-note">Awaiting manager approval</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffLeavePortal;
